/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback, Fragment, memo } from 'react';
import dayjs from 'dayjs';
import Style from '../../css/LoanHistory.module.css';
import { Table, TableHead, TableRow, TableBody, TableCell } from '@mui/material';
import useGetServicingHistory from '../../common/hooks/useGetServcingHistory';
import LoadingModal from '../modals/LoadingModal';
import { useSnackbar } from 'notistack';
import { Sorted } from '../../pages/LoanHistoryPage';

type History = {
  date: any;
  initials: string;
  fields: string;
  before: string;
  after: string;
};

type LoanHistoryHistoryFormProps = {
  loanNumber: string;
  selectedSort: string;
};

const LoanHistoryHistoryForm = memo((props: LoanHistoryHistoryFormProps) => {
  const dateFormatConst = 'MM/DD/YYYY hh:mm';
  const [rows, setRows] = useState([] as History[]);
  const [loading, setLoading] = useState(false);
  const { servicingHistory, servicingHistoryErrorMessages, loadingServicingHistory } = useGetServicingHistory(
    props.loanNumber,
  );

  const { enqueueSnackbar } = useSnackbar();

  const setUpRows = useCallback(() => {
    const history = [];
    //sort the changes based on timestamp this will go from oldest to newest
    servicingHistory.sort((item1, item2) => {
      return item1.timestamp - item2.timestamp;
    });

    servicingHistory.forEach((item) => {
      history.push({
        date: dayjs(item.timestamp * 1000).format(dateFormatConst),
        initials: item.uid,
        fields: item.field,
        before: item.beforeChange,
        after: item.afterChange,
      } as History);
    });
    //we are already in oldest to newest no change for ascending order
    //we can't sort on payee for history
    //reverse will display the payments newest to oldest to the user
    if (props.selectedSort === Sorted.DESC_ORDER) {
      history.reverse();
    }
    setRows(history);
  }, [servicingHistory, props.selectedSort]);

  useEffect(() => {
    if (!loadingServicingHistory) {
      setLoading(false);
      if (servicingHistoryErrorMessages) {
        enqueueSnackbar(servicingHistoryErrorMessages, { variant: 'error' });
      } else {
        setUpRows();
      }
    } else {
      setLoading(true);
    }
  }, [servicingHistoryErrorMessages, loadingServicingHistory, setUpRows, enqueueSnackbar]);

  return (
    <Fragment>
      {loading && <LoadingModal open={loading} />}
      <div className={Style.height}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              <TableCell align='center' className={Style.headerCell}>
                Date
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Initials
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Fields
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Before
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                After
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell component='th' scope='row' align='center' className={Style.cell}>
                  {row.date}
                </TableCell>
                <TableCell align='center' className={Style.cell}>
                  {row.initials}
                </TableCell>
                <TableCell align='center' className={Style.cell}>
                  {row.fields}
                </TableCell>
                <TableCell align='center' className={Style.cell}>
                  {row.before}
                </TableCell>
                <TableCell align='center' className={Style.cell}>
                  {row.after}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Fragment>
  );
});

export default LoanHistoryHistoryForm;
